import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Card, CardBody, Col, Container, CardHeader, Row } from "reactstrap";
import { useParams, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";

import Loader from "../../../../Components/Common/Loader";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import TableContainer from "../../../../Components/Common/TableContainer";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import ExportCSVModal from "../../../../Components/Common/ExportCSVModal";
import { useProfile } from "../../../../Components/Hooks/UserHooks";

import { openUnitModal, closeUnitModal } from "../../../../slices/modal/reducer";
import {
  getUnits as onGetUnits,
  deleteUnit as onDeleteUnit,
  getBlocks as onGetBlocks,
} from "../../../../slices/thunks";

import UnitModal from "./UnitModal";
import UnitFilters from "./components/UnitFilters";
import { buildUnitColumns } from "./utils/columnDefinitions";
import { UNIT_TYPE_OPTIONS } from "../shared/constants/unitTypes";

const PAGE_SIZE = 20;
const DEFAULT_ORDERING = "-created_at";

const NUMERIC_PARAMS = ["floor", "min_price", "max_price"];
const STRING_PARAMS = ["search", "unit_type", "ordering"];

const readParamsFromSearch = (searchParams) => {
  // Defaults match UnitFilters' initial emission so the first dispatch is stable.
  const params = { available: true, ordering: DEFAULT_ORDERING };
  STRING_PARAMS.forEach((k) => {
    const v = searchParams.get(k);
    if (v) params[k] = v;
  });
  NUMERIC_PARAMS.forEach((k) => {
    const v = searchParams.get(k);
    if (v != null && v !== "" && !Number.isNaN(Number(v))) params[k] = Number(v);
  });
  const avail = searchParams.get("available");
  if (avail === "false") params.available = false;
  else if (avail === "true") params.available = true;
  return params;
};

const buildQueryString = (filterParams, page) => {
  const next = new URLSearchParams();
  if (page > 1) next.set("page", String(page));
  Object.entries(filterParams || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    if (k === "ordering" && v === DEFAULT_ORDERING) return;
    if (k === "available" && v === true) return; // default (unsold)
    next.set(k, String(v));
  });
  return next;
};

const paramsEqual = (a, b) => {
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  return ka.length === kb.length && ka.every((k) => a[k] === b[k]);
};

const selectBlockProperties = createSelector(
  (state) => state.Buildings,
  (build) => ({
    blocks: build.blocks,
    isBlockSuccess: build.isBlockSuccess,
    error: build.error,
    loading: build.loading,
  })
);

const selectUnitProperties = createSelector(
  (state) => state.Unit,
  (unit) => ({
    units: unit.units,
    count: unit.count,
    totalPages: unit.totalPages,
    currentPage: unit.currentPage,
    nextPage: unit.nextPage,
    previousPage: unit.previousPage,
    isUnitSuccess: unit.isUnitSuccess,
    error: unit.error,
    loading: unit.loading,
  })
);

const Units = () => {
  const dispatch = useDispatch();
  const {
    units,
    count,
    totalPages,
    currentPage,
    nextPage,
    previousPage,
    error,
    isUnitSuccess,
    loading,
  } = useSelector(selectUnitProperties);
  const { blocks } = useSelector(selectBlockProperties);
  const { blockId } = useParams();
  const { isAdmin } = useProfile();
  const { isUnitModalOpen } = useSelector((state) => state.Modal);

  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = Math.max(1, parseInt(searchParams.get("page") || "1", 10));
  const initialFilterParams = useMemo(() => readParamsFromSearch(searchParams), []);

  const [filterParams, setFilterParams] = useState(initialFilterParams);
  const [page, setPage] = useState(initialPage);

  useEffect(() => {
    const next = buildQueryString(filterParams, page);
    const currentStr = searchParams.toString();
    const nextStr = next.toString();
    if (currentStr !== nextStr) {
      setSearchParams(next, { replace: true });
    }
  }, [filterParams, page, searchParams, setSearchParams]);
  const [showExportCsvModal, setShowExportCsvModal] = useState(false);
  const [unitSelectedForDelete, setUnitSelectedForDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    dispatch(onGetBlocks()).catch((err) =>
      console.error("Failed to fetch blocks: ", err)
    );
  }, [dispatch]);

  useEffect(() => {
    const params = { page, ...filterParams };
    if (blockId) params.block = blockId;
    dispatch(onGetUnits(params)).catch((err) =>
      console.error("Failed to fetch units: ", err)
    );
  }, [dispatch, blockId, page, filterParams]);

  const handleFilterChange = useCallback((params) => {
    setFilterParams((prev) => (paramsEqual(prev, params) ? prev : params));
  }, []);

  // Reset to page 1 when filters actually change (skip the initial emission)
  const skipFirstFilterChange = useRef(true);
  useEffect(() => {
    if (skipFirstFilterChange.current) {
      skipFirstFilterChange.current = false;
      return;
    }
    setPage(1);
  }, [filterParams]);

  const handleCreateUnit = () =>
    dispatch(openUnitModal({ modalType: "create", unit: null }));

  const handleDeleteUnit = (unit) => {
    if (unit) {
      dispatch(onDeleteUnit(unit.id));
      setShowDeleteModal(false);
    }
  };

  const handleCloseModal = () => dispatch(closeUnitModal());

  const handleDeleteRequest = useCallback((unit) => {
    setUnitSelectedForDelete(unit);
    setShowDeleteModal(true);
  }, []);

  const columns = useMemo(
    () => buildUnitColumns({ isAdmin, onDeleteRequest: handleDeleteRequest }),
    [isAdmin, handleDeleteRequest]
  );

  const hasUnits = Array.isArray(units) && units.length > 0;
  const emptyFromFilters = isUnitSuccess && !hasUnits && count === 0;

  return (
    <div className="page-content">
      <ExportCSVModal
        show={showExportCsvModal}
        onCloseClick={() => setShowExportCsvModal(false)}
        data={units}
      />
      <Container fluid>
        <BreadCrumb
          title={`Block ${
            blocks?.find((b) => b.id === parseInt(blockId))?.name || ""
          }`}
          pageTitle="Units"
        />

        <UnitFilters
          typeOptions={UNIT_TYPE_OPTIONS}
          onFilterChange={handleFilterChange}
          initialParams={initialFilterParams}
        />

        <Row>
          <Col lg={12}>
            <Card id="orderList">
              <CardHeader className="border-0">
                <Row className="align-items-center gy-3">
                  <div className="col-sm">
                    <h5 className="card-title mb-0">
                      {count != null ? `${count} Units` : "Units"}
                    </h5>
                  </div>
                  <div
                    className="col-sm-auto"
                    style={{ marginBottom: "10px" }}
                  >
                    <div className="d-flex gap-1 flex-wrap">
                      {isAdmin && (
                        <button
                          type="button"
                          className="btn btn-success add-btn"
                          id="create-btn"
                          onClick={handleCreateUnit}
                        >
                          <i className="ri-add-line align-bottom me-1"></i>
                          Create Unit
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-info"
                        onClick={() => setShowExportCsvModal(true)}
                      >
                        <i className="ri-file-download-line align-bottom me-1"></i>
                        Export
                      </button>
                    </div>
                  </div>
                </Row>
              </CardHeader>

              <CardBody className="pt-0">
                {loading && !hasUnits ? (
                  <Loader error={error} />
                ) : hasUnits ? (
                  <>
                    <TableContainer
                      columns={columns}
                      data={units}
                      customPageSize={PAGE_SIZE}
                      divClass="table-responsive table-card mb-1"
                      tableClass="align-middle table-nowrap"
                      theadClass="table-light text-muted text-uppercase"
                      isUnitFilter={true}
                      showPagination={false}
                      SearchPlaceholder="Search for any field"
                    />
                    <Row className="align-items-center justify-content-between mt-3">
                      <Col xs="auto">
                        <span className="text-muted">
                          Page {currentPage} of {totalPages}
                          {count != null ? ` · ${count} total` : ""}
                        </span>
                      </Col>
                      <Col xs="auto">
                        <ul className="pagination pagination-separated mb-0">
                          <li
                            className={`page-item ${
                              previousPage == null ? "disabled" : ""
                            }`}
                          >
                            <button
                              type="button"
                              className="page-link"
                              onClick={() =>
                                previousPage != null && setPage(previousPage)
                              }
                              disabled={previousPage == null}
                            >
                              <i className="ri-arrow-left-s-line" />
                            </button>
                          </li>
                          <li className="page-item active">
                            <span className="page-link">{currentPage}</span>
                          </li>
                          <li
                            className={`page-item ${
                              nextPage == null ? "disabled" : ""
                            }`}
                          >
                            <button
                              type="button"
                              className="page-link"
                              onClick={() =>
                                nextPage != null && setPage(nextPage)
                              }
                              disabled={nextPage == null}
                            >
                              <i className="ri-arrow-right-s-line" />
                            </button>
                          </li>
                        </ul>
                      </Col>
                    </Row>
                  </>
                ) : emptyFromFilters ? (
                  <div className="text-center mt-5">
                    <p className="text-muted">
                      No units match these filters.
                    </p>
                  </div>
                ) : (
                  <div className="text-center mt-5">
                    <p className="text-muted">
                      No units are introduced yet. Please add a new unit.
                    </p>
                  </div>
                )}

                <UnitModal
                  show={isUnitModalOpen}
                  onCloseClick={handleCloseModal}
                  unit={undefined}
                  blocks={blocks}
                />
                <DeleteModal
                  show={showDeleteModal}
                  onDeleteClick={() => handleDeleteUnit(unitSelectedForDelete)}
                  onCloseClick={() => setShowDeleteModal(false)}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Units;
